<template>
    <v-container class="pa-5" fluid>
        <page-title>
            {{ pageTitle }}
            <span :class="calculationStatusClass" class="text-uppercase text-body-2">
                {{ calculationStatus.name }}
            </span>
        </page-title>

        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" xl="10" offset-xl="1">
                <v-container fluid>
                    <v-stepper v-model="stepper">
                        <v-progress-linear
                            color="blue"
                            indeterminate
                            :active="actionLoading.updateCalculation"
                            absolute
                            height="3"
                        />
                        <v-overlay :value="actionLoading.updateCalculation" color="white" opacity="0.4" absolute />

                        <v-stepper-header class="no-shadow">
                            <v-stepper-step step="1" :complete="stepper > 1" :color="stepper > 1 ? 'success' : 'primary'">
                                Klient i produkty
                            </v-stepper-step>

                            <v-divider />

                            <v-stepper-step step="2" :complete="stepper > 2" :color="stepper > 2 ? 'success' : 'primary'">
                                Montaże
                            </v-stepper-step>

                            <v-divider />

                            <v-stepper-step step="3" color="primary">
                                Podsumowanie
                            </v-stepper-step>
                        </v-stepper-header>

                        <v-stepper-items>
                            <v-stepper-content step="1" class="pt-0">
                                <CalculationParameters
                                    :is-visible="stepper === 1"
                                    :is-calculation-editable="isCalculationEditable"
                                    :papers="papers"
                                    @next-step="nextStep"
                                    @calculation-loading="toggleCalculationLoading"
                                />
                            </v-stepper-content>


                            <v-stepper-content step="2" class="pt-0">
                                <CalculationImpositions
                                    :is-visible="stepper === 2"
                                    :is-calculation-editable="isCalculationEditable"
                                    :machines="machines"
                                    :papers="papers"
                                    :color-templates="colorTemplates"
                                    :elastic-materials="elasticMaterials"
                                    @show-calculation-parameters="showCalculationParameters"
                                    @show-calculation-summary="showCalculationSummary"
                                    @calculation-loading="toggleCalculationLoading"
                                    @is-calculation-validated="isCalculationValid"
                                />
                            </v-stepper-content>

                            <v-stepper-content step="3" class="pt-0">
                                <CalculationSummary
                                    :is-visible="stepper === 3"
                                    :is-calculation-editable="isCalculationEditable"
                                    :is-calculation-invalid="isCalculationImpositionsInvalid"
                                    @calculation-loading="toggleCalculationLoading"
                                    @show-calculation-impositions="showCalculationImpositions"
                                />
                            </v-stepper-content>
                        </v-stepper-items>
                    </v-stepper>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import store from '@/./store'

import PageTitle from '@/components/common/PageTitle'

import CalculationParameters from './CalculationParameters/Index'
import CalculationImpositions from './CalculationImpositions/Index'
import CalculationSummary from './CalculationSummary/Index'

import { errorHandler } from '@/utils/errorHandler'

import { appConfig } from '@/config'

export default {
    name: 'Calculation',
    components: {
        PageTitle,
        CalculationParameters,
        CalculationImpositions,
        CalculationSummary
    },
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('configurations/getCalculationMachines', {
                query: '?isVisible=true',
                shouldSavedInStore: false
            }),
            store.dispatch('configurations/getPapers', {
                shouldSavedInStore: false
            }),
            store.dispatch('calculations/getCalculationColorTemplates'),
            store.dispatch('configurations/getMaterials', {
                query: '?isVisible=true&materialType_id=24',
                shouldSavedInStore: false
            }),
            store.dispatch('configurations/tools/getCalculationDieCutters'),
            store.dispatch('configurations/tools/getDieCuttersOwnershipTypes'),
            store.dispatch('configurations/getCertificates')
        ])
            .then(responses => {
                // Create Calculation
                if (to.params.calculationNumber) {
                    next(vm => {
                        vm.pageTitle = `${to.meta.title}: ${to.params.calculationNumber}`
                        vm.machines = responses[0].data.results
                        vm.papers = responses[1].data.results
                        vm.colorTemplates = responses[2].data.results
                        vm.elasticMaterials = responses[3].data.results
                    })
                // Open Calculation
                } else {
                    store.dispatch('calculations/getCalculation', to.params.id)
                        .then(response => {
                            next(vm => {
                                vm.pageTitle = `${to.meta.title}: ${response.data.number}`
                                vm.machines = responses[0].data.results
                                vm.papers = responses[1].data.results
                                vm.colorTemplates = responses[2].data.results
                                vm.elasticMaterials = responses[3].data.results
                            })
                        })
                        .catch(error => {
                            next(error)
                            errorHandler(error)
                        })
                }
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    data() {
        return {
            pageTitle: '',
            stepper: 1,
            machines: [],
            papers: [],
            colorTemplates: [],
            elasticMaterials: [],
            isCalculationImpositionsInvalid: false,
            actionLoading: {
                updateCalculation: false
            },
        }
    },
    computed: {
        calculation() {
            return this.$store.state.calculations.calculations.calculation
        },
        calculationStatus() {
            return this.calculation.status
        },
        isCalculationEditable() {
            return this.calculationStatus.id === 10 // Nowa
        },
        calculationStatusClass() {
            if (!this.calculationStatus.id) return

            let calculationStatusClass = ''

            switch (this.calculationStatus.id) {
                case 10: // Nowa
                    calculationStatusClass = 'info--text'
                    break
                case 15: // Zatwierdzona
                    calculationStatusClass = 'success--text'
                    break
                default:
                    break
            }

            return calculationStatusClass
        },
    },
    methods: {
        recalculateAllPackagesAmount() {
            const impositions = JSON.parse(JSON.stringify(this.$store.state.calculations.calculations.calculation.calculationImpositions))

            impositions.forEach(imposition => {
                const packingTask = imposition.calculationImpositionTasks.find(task => {
                    return task.processId === appConfig.common.processes.packingTaskId
                })

                if (packingTask && packingTask.calculationImpositionTaskPackages) {
                    if (packingTask.calculationImpositionTaskPackages.length) {
                        packingTask.calculationImpositionTaskPackages.forEach(packageData => {
                            const packageMaxWeightCapacity = packageData.packageType.maxWeightCapacity

                            if (packageData.calculationImpositionTaskPackageWares.length > 0) {
                                let waresWeight = 0

                                packageData.calculationImpositionTaskPackageWares.forEach(ware => {
                                    waresWeight += ware.calculationProduct.weight * ware.amount
                                })

                                packageData.packageAmount = Math.ceil(waresWeight / packageMaxWeightCapacity)
                            } else {
                                packageData.packageAmount = 1
                            }
                        })
                        this.$store.dispatch('calculations/updateImposition', imposition)
                    }
                }
            })
        },
        nextStep() {
            if (this.isCalculationEditable) {
                this.actionLoading.updateCalculation = true

                this.$store.dispatch('calculations/createNewImpositions')

                this.recalculateAllPackagesAmount()

                this.$store.dispatch('calculations/updateCalculation', this.calculation)
                    .then(() => {
                        this.stepper++
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.actionLoading.updateCalculation = false
                    })
            } else {
                this.stepper++
            }
        },
        showCalculationParameters() {
            this.stepper = 1
        },
        showCalculationImpositions() {
            this.stepper = 2
        },
        showCalculationSummary() {
            this.stepper = 3
        },
        toggleCalculationLoading(value) {
            this.actionLoading.updateCalculation = value
        },
        close() {
            router.push({ name: 'calculationsCalculations' })
                .catch(error => {
                    errorHandler(error)
                })
        },
        isCalculationValid(isCalculationValidated) {
            this.isCalculationImpositionsInvalid = isCalculationValidated
        },
    }
}
</script>